import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { business, heroImage } from "@/config/site";

const line = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.18, ease: [0.22, 1, 0.36, 1] },
  }),
};

const headingLines = ["Crafting", "Extraordinary", "Celebrations"];

export const Hero = ({ onBook }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        {heroImage ? (
          <img
            src={heroImage}
            alt="Luxury Indian wedding stage decoration by Aarambh.KD"
            className="h-full w-full object-cover"
            fetchPriority="high"
          />
        ) : (
          // Elegant luxury gradient placeholder (no stock photos). Set `heroImage` in config to use your own.
          <div className="h-full w-full bg-gradient-to-br from-maroon via-[#3d1414] to-charcoal">
            <div className="absolute inset-0 opacity-[0.18] bg-[radial-gradient(circle_at_25%_25%,#C9A227,transparent_55%)]" />
            <div className="absolute inset-0 opacity-[0.12] bg-[radial-gradient(circle_at_80%_70%,#C9A227,transparent_50%)]" />
          </div>
        )}
      </motion.div>
      <motion.div
        style={{ opacity: overlayOpacity }}
        className={`absolute inset-0 ${
          heroImage
            ? "bg-gradient-to-b from-black/40 via-black/45 to-black/85"
            : "bg-gradient-to-b from-black/10 via-transparent to-black/45"
        }`}
      />

      <div className="relative z-10 flex h-full flex-col justify-end pb-20 sm:pb-28">
        <div className="mx-auto w-full max-w-7xl px-5 sm:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 1 }}
            className="mb-5 flex items-center gap-3 text-xs sm:text-sm tracking-[0.3em] uppercase text-champagne"
          >
            <span className="h-px w-10 bg-champagne inline-block" />
            {business.fullName}
          </motion.p>

          <h1 className="font-display text-white text-[15vw] leading-[0.92] sm:text-7xl lg:text-8xl">
            {headingLines.map((l, i) => (
              <span key={l} className="block overflow-hidden">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="visible"
                  className={`block ${i === 1 ? "italic text-champagne" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-7 max-w-2xl text-sm sm:text-base leading-relaxed text-white/80"
          >
            From dream weddings to corporate events, Aarambh.KD transforms every
            occasion into an unforgettable experience — complete event planning,
            elegant décor, premium photography, tent house infrastructure,
            catering coordination and flawless execution.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.5, duration: 0.9 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-book-btn"
              onClick={onBook}
              className="rounded-full bg-champagne px-8 py-3.5 text-sm font-medium text-charcoal transition-all duration-300 hover:bg-white hover:scale-[1.03]"
            >
              Book Consultation
            </button>
            <button
              data-testid="hero-work-btn"
              onClick={() =>
                document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" })
              }
              className="rounded-full border border-white/50 px-8 py-3.5 text-sm font-medium text-white backdrop-blur-sm transition-all duration-300 hover:border-champagne hover:text-champagne"
            >
              View Our Work
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-white/70"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
        >
          <ChevronDown className="h-6 w-6" strokeWidth={1.5} />
        </motion.div>
      </motion.div>
    </section>
  );
};
